/**
 * Hook for managing file uploads
 *
 * Supports:
 * - Single file upload with presigned URLs
 * - Multipart upload for large files (>100MB)
 * - Progress tracking
 * - Upload cancellation
 * - Retry on failure
 */

import { useState, useCallback, useRef } from 'react';
import {
  api,
  FileCategory,
  UserFileResponse,
  PresignedUploadResponse,
  MultipartUploadResponse,
} from '../api';

const MAX_SINGLE_UPLOAD_SIZE = 100 * 1024 * 1024; // 100MB

export interface UploadProgress {
  fileId: string;
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed' | 'cancelled';
  error?: string;
  file?: UserFileResponse;
}

export interface UseFileUploadOptions {
  /** Default category for uploads */
  defaultCategory?: FileCategory;
  /** Default folder for uploads */
  defaultFolderId?: string;
  /** Callback when upload completes */
  onUploadComplete?: (file: UserFileResponse) => void;
  /** Callback when upload fails */
  onUploadError?: (fileName: string, error: Error) => void;
  /** Callback for progress updates */
  onProgress?: (progress: UploadProgress[]) => void;
}

export interface UseFileUploadResult {
  /** Current upload progress for all files */
  uploads: UploadProgress[];
  /** Whether any upload is in progress */
  isUploading: boolean;
  /** Upload files */
  uploadFiles: (files: File[], options?: {
    category?: FileCategory;
    folderId?: string;
    tags?: string[];
  }) => Promise<void>;
  /** Cancel an upload */
  cancelUpload: (fileId: string) => void;
  /** Clear completed/failed uploads from the list */
  clearCompleted: () => void;
  /** Retry a failed upload */
  retryUpload: (fileId: string) => Promise<void>;
}

/**
 * Hook for managing file uploads
 */
export function useFileUpload(options: UseFileUploadOptions = {}): UseFileUploadResult {
  const {
    defaultCategory = 'general',
    defaultFolderId,
    onUploadComplete,
    onUploadError,
    onProgress,
  } = options;

  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const pendingFilesRef = useRef<Map<string, { file: File; options: { category?: FileCategory; folderId?: string; tags?: string[] } }>>(new Map());

  const updateUpload = useCallback((fileId: string, update: Partial<UploadProgress>) => {
    setUploads((prev) => {
      const updated = prev.map((u) =>
        u.fileId === fileId ? { ...u, ...update } : u
      );
      onProgress?.(updated);
      return updated;
    });
  }, [onProgress]);

  const uploadSingleFile = useCallback(async (
    file: File,
    presigned: PresignedUploadResponse,
    abortSignal: AbortSignal,
    options: { category?: FileCategory; folderId?: string; tags?: string[] }
  ): Promise<UserFileResponse> => {
    // Upload to S3
    const response = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
      signal: abortSignal,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    // Confirm upload
    updateUpload(presigned.fileId, { status: 'processing', progress: 100 });

    const { file: confirmedFile } = await api.confirmUpload(presigned.fileId, {
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      s3Key: presigned.s3Key,
      category: options.category || defaultCategory,
      folderId: options.folderId || defaultFolderId,
      tags: options.tags,
    });

    return confirmedFile;
  }, [defaultCategory, defaultFolderId, updateUpload]);

  const uploadMultipartFile = useCallback(async (
    file: File,
    multipart: MultipartUploadResponse,
    abortSignal: AbortSignal,
    options: { category?: FileCategory; folderId?: string; tags?: string[] }
  ): Promise<UserFileResponse> => {
    const completedParts: Array<{ partNumber: number; etag: string }> = [];
    let uploadedBytes = 0;

    // Upload each part
    for (let partNumber = 1; partNumber <= multipart.totalParts; partNumber++) {
      if (abortSignal.aborted) {
        throw new Error('Upload cancelled');
      }

      // Get presigned URL for this part
      const partPresigned = await api.getMultipartPartUrl({
        uploadId: multipart.uploadId,
        fileId: multipart.fileId,
        s3Key: multipart.s3Key,
        partNumber,
      });

      // Calculate part boundaries
      const startByte = (partNumber - 1) * multipart.partSize;
      const endByte = Math.min(startByte + multipart.partSize, file.size);
      const partBlob = file.slice(startByte, endByte);

      // Upload the part
      const response = await fetch(partPresigned.uploadUrl, {
        method: 'PUT',
        body: partBlob,
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`Part ${partNumber} upload failed with status ${response.status}`);
      }

      const etag = response.headers.get('ETag');
      if (!etag) {
        throw new Error(`Part ${partNumber} missing ETag header`);
      }

      completedParts.push({
        partNumber,
        etag: etag.replace(/"/g, ''),
      });

      uploadedBytes += partBlob.size;
      const progress = Math.round((uploadedBytes / file.size) * 100);
      updateUpload(multipart.fileId, { progress });
    }

    // Complete multipart upload
    await api.completeMultipartUpload({
      uploadId: multipart.uploadId,
      fileId: multipart.fileId,
      s3Key: multipart.s3Key,
      parts: completedParts,
    });

    // Confirm upload
    updateUpload(multipart.fileId, { status: 'processing', progress: 100 });

    const { file: confirmedFile } = await api.confirmUpload(multipart.fileId, {
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      s3Key: multipart.s3Key,
      category: options.category || defaultCategory,
      folderId: options.folderId || defaultFolderId,
      tags: options.tags,
    });

    return confirmedFile;
  }, [defaultCategory, defaultFolderId, updateUpload]);

  const uploadFile = useCallback(async (
    file: File,
    options: { category?: FileCategory; folderId?: string; tags?: string[] }
  ): Promise<void> => {
    const abortController = new AbortController();
    let fileId: string;

    try {
      if (file.size <= MAX_SINGLE_UPLOAD_SIZE) {
        // Single upload
        const presigned = await api.getUploadUrl({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          category: options.category || defaultCategory,
        });

        fileId = presigned.fileId;
        abortControllersRef.current.set(fileId, abortController);
        pendingFilesRef.current.set(fileId, { file, options });

        // Add to uploads list
        setUploads((prev) => [
          ...prev,
          {
            fileId,
            fileName: file.name,
            progress: 0,
            status: 'uploading',
          },
        ]);

        const confirmedFile = await uploadSingleFile(
          file,
          presigned,
          abortController.signal,
          options
        );

        updateUpload(fileId, {
          status: 'completed',
          progress: 100,
          file: confirmedFile,
        });

        onUploadComplete?.(confirmedFile);
      } else {
        // Multipart upload
        const multipart = await api.initiateMultipartUpload({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          category: options.category || defaultCategory,
        });

        fileId = multipart.fileId;
        abortControllersRef.current.set(fileId, abortController);
        pendingFilesRef.current.set(fileId, { file, options });

        // Add to uploads list
        setUploads((prev) => [
          ...prev,
          {
            fileId,
            fileName: file.name,
            progress: 0,
            status: 'uploading',
          },
        ]);

        const confirmedFile = await uploadMultipartFile(
          file,
          multipart,
          abortController.signal,
          options
        );

        updateUpload(fileId, {
          status: 'completed',
          progress: 100,
          file: confirmedFile,
        });

        onUploadComplete?.(confirmedFile);
      }
    } catch (error) {
      if (error instanceof Error && error.message === 'Upload cancelled') {
        // Already handled
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      updateUpload(fileId!, {
        status: 'failed',
        error: errorMessage,
      });

      onUploadError?.(file.name, error instanceof Error ? error : new Error(errorMessage));
    } finally {
      abortControllersRef.current.delete(fileId!);
    }
  }, [defaultCategory, uploadSingleFile, uploadMultipartFile, updateUpload, onUploadComplete, onUploadError]);

  const uploadFiles = useCallback(async (
    files: File[],
    options: { category?: FileCategory; folderId?: string; tags?: string[] } = {}
  ): Promise<void> => {
    // Upload files in parallel (limit concurrency if needed)
    await Promise.all(files.map((file) => uploadFile(file, options)));
  }, [uploadFile]);

  const cancelUpload = useCallback((fileId: string) => {
    const controller = abortControllersRef.current.get(fileId);
    if (controller) {
      controller.abort();
      updateUpload(fileId, { status: 'cancelled' });
      abortControllersRef.current.delete(fileId);
      pendingFilesRef.current.delete(fileId);
    }
  }, [updateUpload]);

  const clearCompleted = useCallback(() => {
    setUploads((prev) =>
      prev.filter((u) => u.status === 'uploading' || u.status === 'pending')
    );
  }, []);

  const retryUpload = useCallback(async (fileId: string) => {
    const pending = pendingFilesRef.current.get(fileId);
    if (!pending) {
      return;
    }

    // Remove the failed upload
    setUploads((prev) => prev.filter((u) => u.fileId !== fileId));
    pendingFilesRef.current.delete(fileId);

    // Retry
    await uploadFile(pending.file, pending.options);
  }, [uploadFile]);

  const isUploading = uploads.some(
    (u) => u.status === 'uploading' || u.status === 'processing'
  );

  return {
    uploads,
    isUploading,
    uploadFiles,
    cancelUpload,
    clearCompleted,
    retryUpload,
  };
}

export default useFileUpload;
